import { css, cx } from '@emotion/css';
import type { HTMLAttributes } from 'react';
import { FiMapPin, FiCalendar, FiUsers } from 'react-icons/fi';
import { formatTrustedDate, formatTrustedTime } from '../../../../shared/utils/datetime';
import { useGetOrderSession, useGetSchedule, useGetUserOrder } from '../../hooks/checkout-api.hooks';

export interface CartSeat {
  id: string;
  row?: string;
  column?: string;
  zone?: string;
  ticketType?: string;
  price: number;
  selected?: boolean;
  ticketId?: string;
  isMultiSeat?: boolean;
  seatsPerTicket?: number;
  multiGroupId?: string;
}

export interface AppliedCoupon {
  discount: number;
  code: string;
}

export interface CartSummaryProps extends HTMLAttributes<HTMLDivElement> {
  sessionId?: string;
  orderId?: string;
  // Legacy props kept for compatibility but ignored: we always use session/order data
  selectedSeats?: CartSeat[];
  installments?: number;
  appliedCoupon?: AppliedCoupon | null;
  onTotalCalculated?: (total: number) => void;
}

// Styles
const container = css({
  width: '100%',
  backgroundColor: 'var(--primary-color)',
  borderRadius: 'var(--spacing-md)',
  boxShadow: 'var(--shadow-300)',
  overflow: 'hidden',
  position: 'sticky',
  top: 'var(--spacing-xl)',
  border: '1px solid rgba(var(--pcrgba-200), 0.5)',
  '@media (max-width: 920px)': {
    position: 'static',
    marginTop: 'var(--spacing-xl)',
  },
});

const header = css({
  width: '100%',
  fontSize: 'var(--fs-450)',
  fontWeight: 600,
  backgroundColor: 'var(--pc-700)',
  color: 'var(--primary-color)',
  padding: 'var(--spacing-sm) var(--spacing-md)',
});

const content = css({
  display: 'flex',
  flexDirection: 'column',
  padding: 'var(--spacing-md)',
  gap: 'var(--spacing-md)',
});

const separator = css({
  height: '1px',
  backgroundColor: 'rgba(var(--pcrgba-200), 0.7)',
  border: 'none',
  margin: 'var(--spacing-2xs) 0',
});

const eventInfo = css({
  display: 'flex',
  gap: 'var(--spacing-md)',
  '@media (max-width: 480px)': {
    flexDirection: 'column',
  },
});

const eventPoster = css({
  borderRadius: 'var(--spacing-sm)',
  width: '100px',
  aspectRatio: '4/4',
  backgroundColor: 'rgba(var(--pcrgba-100), 0.3)',
  overflow: 'hidden',
  border: '1px solid rgba(var(--pcrgba-200), 0.5)',
  flexShrink: 0,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  '@media (max-width: 480px)': {
    width: '100%',
    aspectRatio: '16/9',
    maxHeight: '120px',
  },
});

const eventDetails = css({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-xs)',
});

const eventTitle = css({
  fontSize: 'var(--fs-400)',
  fontWeight: 600,
  color: 'var(--pc-700)',
  lineHeight: 1.3,
  '& em': {
    color: 'var(--pc-500)',
    fontStyle: 'normal',
  },
});

const eventMeta = css({
  display: 'flex',
  alignItems: 'center',
  fontSize: 'var(--fs-350)',
  color: 'var(--pc-400)',
  gap: 'var(--spacing-xs)',
  '& svg': {
    width: 'var(--fs-350)',
    height: 'var(--fs-350)',
    color: 'var(--pc-500)',
    flexShrink: 0,
  },
});

const seatsList = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--spacing-2xs)',
});

const seatItem = css({
  backgroundColor: 'rgba(var(--pcrgba-100), 0.4)',
  color: 'var(--pc-600)',
  padding: 'var(--spacing-3xs) var(--spacing-2xs)',
  borderRadius: 'var(--spacing-xs)',
  fontSize: 'var(--fs-300)',
  fontWeight: 500,
  border: '1px solid rgba(var(--pcrgba-300), 0.5)',
});

const summaryBox = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-2xs)',
  padding: 'var(--spacing-sm)',
  backgroundColor: 'rgba(var(--pcrgba-100), 0.2)',
  borderRadius: 'var(--spacing-md)',
  border: '1px solid rgba(var(--pcrgba-200), 0.5)',
});

const summaryRow = css({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: 'var(--fs-350)',
  color: 'var(--pc-600)',
  padding: 'var(--spacing-2xs) 0',
  '& b': {
    color: 'var(--pc-700)',
    fontWeight: 500,
  },
  '@media (max-width: 480px)': {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 'var(--spacing-4xs)',
  },
});

const summaryTotal = css({
  fontSize: 'var(--fs-400)',
  fontWeight: 600,
  color: 'var(--pc-700)',
  paddingTop: 'var(--spacing-xs)',
  '& b': {
    color: 'var(--pc-700)',
  },
});

const installmentInfo = css({
  textAlign: 'right',
  paddingTop: 'var(--spacing-4xs)',
  '& small': {
    color: 'var(--pc-500)',
    fontSize: 'var(--fs-300)',
  },
});

const ticketCount = css({
  display: 'flex',
  alignItems: 'center',
  fontSize: 'var(--fs-300)',
  color: 'var(--pc-500)',
  marginLeft: 'var(--spacing-xs)',
  padding: 'var(--spacing-4xs) var(--spacing-2xs)',
  backgroundColor: 'rgba(var(--pcrgba-100), 0.6)',
  borderRadius: 'var(--spacing-xs)',
  gap: 'var(--spacing-4xs)',
  '& svg': {
    color: 'var(--pc-500)',
  },
  '@media (max-width: 480px)': {
    marginLeft: 0,
    marginTop: 'var(--spacing-2xs)',
  },
});

export function CartSummary({
  sessionId,
  orderId,
  // Legacy props ignored to enforce session/order-driven summary
  selectedSeats = [],
  installments = 1,
  appliedCoupon = null,
  onTotalCalculated,
  className,
  style,
  ...props
}: CartSummaryProps) {
  // Determine which data source to use: order (for payment success) or session (for checkout flow)
  const useOrderData = !!orderId && !sessionId;
  
  // Get session data (for checkout flow)
  const { data: sessionData } = useGetOrderSession(sessionId || '', !!sessionId);
  
  // Get order data (for payment success/completion)  
  const { data: orderData } = useGetUserOrder(orderId || '', useOrderData);
  
  // Extract scheduleId from either source
  let scheduleId = sessionData?.session?.metadata?.scheduleId;
  
  // If using order data, extract scheduleId from first order item
  if (useOrderData && orderData?.order?.items?.[0]) {
    scheduleId = orderData.order.items[0].scheduleId;
  }

  // Get schedule details
  const { data: scheduleData } = useGetSchedule(scheduleId || '', !!scheduleId);

  // Extract event data
  const event = scheduleData?.schedule?.event;
  const location = scheduleData?.schedule?.location;
  const scheduleDate = scheduleData?.schedule?.date;

  // Build seats and totals from the appropriate source
  const items = useOrderData 
    ? (orderData?.order?.items || [])
    : (sessionData?.session?.items || []);
    
  const totalSeats = items.reduce((sum: number, item: any) => sum + (item?.seats?.length || 0), 0);
  const multiSeatTicketCount = items.filter((item: any) => (item?.seats?.length || 0) > 1).length;
  
  const totalAmount = useOrderData 
    ? Number.parseFloat(orderData?.order?.totalAmount || '0')
    : Number.parseFloat(sessionData?.session?.totalAmount || '0');

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  return (
    <div className={cx(container, className)} style={style} {...props}>
      <h2 className={header}>Resumo da Compra</h2>

      <div className={content}>
        <div className={eventInfo}>
          <div className={eventPoster}>
            {event?.posterImage && (
              <img 
                src={`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'}/assets/${event.posterImage.name}${event.posterImage.extension}`} 
                alt={event.title || 'Evento'} 
              />
            )}
          </div>

          <div className={eventDetails}>
            <h3 className={eventTitle}>
              {event?.title || (useOrderData ? 'Pedido Confirmado' : 'Nome do Evento')}
            </h3>

            {location && (
              <div className={eventMeta}>
                <FiMapPin size={16} />
                <span>{location.name} - {location.city}, {location.state}</span>
              </div>
            )}

            {scheduleDate && (
              <div className={eventMeta}>
                <FiCalendar size={16} />
                <span>
                  {formatTrustedDate(scheduleDate)} - {formatTrustedTime(scheduleDate)}
                </span>
              </div>
            )}
          </div>
        </div>

        {items.length > 0 && (
          <>
            <hr className={separator} />

            <div className={seatsList}>
              {items.flatMap((item: any, idx: number) => {
                const ticketType = item?.ticketType || 'Inteira';
                const seats = item?.seats || [];
                if (!seats.length) {
                  return [
                    <div key={`item-${idx}`} className={seatItem}>
                      {ticketType}
                    </div>
                  ];
                }
                return seats.map((s: any, sidx: number) => (
                  <div key={`item-${idx}-seat-${sidx}`} className={seatItem}>
                    {ticketType} · {s.zone || 'Geral'} · {s.row || ''}{s.column || ''}
                  </div>
                ));
              })}
            </div>
          </>
        )}

        <div className={summaryBox}>
          <div className={summaryRow}>
            <b>Quantidade</b>
            <p>
              {totalSeats} assento{totalSeats !== 1 ? 's' : ''}
              {multiSeatTicketCount > 0 && (
                <span className={ticketCount}>
                  <FiUsers size={14} />
                  {multiSeatTicketCount} ingresso
                </span>
              )}
            </p>
          </div>

          <div className={cx(summaryRow, summaryTotal)}>
            <b>Total</b>
            <p>{formatCurrency(totalAmount)}</p>
          </div>

          <div className={installmentInfo}>
            <small>{useOrderData ? 'Pedido confirmado' : 'Valores baseados na sessão atual'}</small>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CartSummary;
